import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Trash2, Plus, Minus, ArrowRight, ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <ShoppingBag className="mx-auto text-[#D4C4B5] mb-6" size={80} />
          <h1 className="text-4xl font-serif font-bold text-[#2C1810] mb-4">
            Votre panier est vide
          </h1>
          <p className="text-[#5D4037] text-lg mb-8">
            Découvrez nos produits de beauté et ajoutez vos favoris au panier
          </p>
          <Link
            to="/produits"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-white rounded-full hover:bg-[#A67B5B] transition-colors font-semibold"
          >
            <ShoppingBag size={20} />
            Découvrir nos produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mb-2">
            Votre Panier
          </h1>
          <p className="text-[#5D4037]">
            {cart.length} article{cart.length > 1 ? 's' : ''} dans votre panier
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-6">
                  {/* Product Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-[#2C1810]">
                          {item.name}
                        </h3>
                        <p className="text-sm text-[#A67B5B]">{item.brand}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-600 hover:text-red-700 transition-colors p-2"
                        aria-label="Retirer du panier"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#D4C4B5] text-[#2C1810] hover:bg-[#F5F0EB] transition-colors"
                          aria-label="Diminuer quantité"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="text-lg font-semibold text-[#2C1810] w-12 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.stock}
                          className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#D4C4B5] text-[#2C1810] hover:bg-[#F5F0EB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Augmenter quantité"
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#C9A96E]">
                          {(item.price * item.quantity).toFixed(2)}€
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-sm text-[#5D4037]">
                            {item.price.toFixed(2)}€ / unité
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Stock Warning */}
                    {item.quantity >= item.stock && (
                      <p className="text-sm text-orange-600 mt-2">
                        Stock maximum atteint ({item.stock} disponible{item.stock > 1 ? 's' : ''})
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Clear Cart Button */}
            <button
              onClick={() => {
                if (window.confirm('Voulez-vous vraiment vider le panier ?')) {
                  clearCart();
                }
              }}
              className="w-full py-3 text-red-600 hover:text-red-700 font-semibold transition-colors"
            >
              Vider le panier
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-6">
                Récapitulatif
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#5D4037]">
                  <span>Sous-total</span>
                  <span className="font-semibold">{getCartTotal().toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-[#5D4037]">
                  <span>Livraison</span>
                  <span className="font-semibold text-[#C9A96E]">Retrait en magasin</span>
                </div>
                <div className="border-t border-[#E8DDD4] pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-semibold text-[#2C1810]">Total</span>
                    <span className="text-3xl font-bold text-[#C9A96E]">
                      {getCartTotal().toFixed(2)}€
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Link
                  to="/commande"
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A96E] text-white rounded-full hover:bg-[#A67B5B] transition-colors font-semibold text-center"
                >
                  Commander
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/produits"
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 border-2 border-[#D4C4B5] text-[#2C1810] rounded-full hover:bg-[#F5F0EB] transition-colors font-semibold text-center"
                >
                  <ArrowLeft size={20} />
                  Continuer mes achats
                </Link>
              </div>

              <div className="mt-6 p-4 bg-[#F5F0EB] rounded-lg">
                <p className="text-sm text-[#5D4037] text-center">
                  💳 Paiement en magasin lors du retrait
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
